/**
 * Words the server does not have yet, kept beyond the field that holds them.
 *
 * A field of the presentation editor - a line's words, its speaker, its
 * delivery or length, the strategist's note, a shot's action, description or
 * cut - holds the person's draft until the document read back carries it,
 * normally a second. Leaving the page is a save too (`useAutosaveField`
 * flushes every dirty field as it unmounts, and sends it with `keepalive`
 * when the tab is hidden or closed, `leave-flush.ts`), but a save can still
 * fail or be refused after the field is gone, and a draft waiting for "Keep
 * mine" or "Use theirs" is not saved at all until the person chooses. Those
 * words must not die with the field. So every draft that differs from what
 * is stored is mirrored here, keyed by storyboard, line and field, and let
 * go once the store has it.
 *
 * Two copies, for two ways of coming back:
 *
 *  - **In memory, for this visit of the app.** A link in the sidebar, a
 *    breadcrumb, the browser's Back: the page unmounts, the app does not. A
 *    field that mounts again takes its draft up at once and settles it as
 *    after a refusal - saved on top when nobody else touched that field,
 *    asked about when somebody did, let go when the store already says the
 *    same.
 *  - **In `localStorage`, for after the page itself is gone** - a reload, a
 *    closed tab, a crash, the laptop lid closed overnight - when a save sent
 *    on the way out may never have arrived and nobody was left to hear so.
 *    `localStorage` rather than `sessionStorage`, because the session copy
 *    died with the very tab whose save it was meant to cover. A copy older
 *    than seven days is dropped unread: by then the script has moved on and
 *    the words would only confuse. Such a draft is never taken up behind the
 *    person's back: every presentation page of that storyboard asks once, in
 *    one quiet line, "You left 1 unsaved change - Restore | Discard"
 *    (`LeftDraftsLine`), and only while it still differs from the stored
 *    value. A page that has the draft's field puts it back on Restore; one
 *    that does not (the summary, a note asked about on the script editor)
 *    offers to open the line page where the field is.
 *
 * `localStorage` is shared by every tab of the app, so a draft is only
 * "left" once the tab that wrote it is gone: each tab that holds drafts
 * writes a heartbeat beside them, stops it on `pagehide`, and a draft whose
 * tab has not beaten for a while is an earlier visit's. Without that, a
 * second tab on the summary would offer to "restore" the words being typed
 * in the first one.
 *
 * Each draft carries a `probe` - which line or shot, which field, and the
 * value as it would be sent - so a page without the field (the summary) can
 * still tell a draft the server already has (the save on the way out did
 * arrive, the common case) from one it does not, and only ever ask about
 * the second kind.
 *
 * Each stored draft names the account that typed it (`setDraftOwner`, set by
 * `StoryboardGate` from the session), and only that account is ever offered
 * it: on a shared browser, or after an admin's view-as, another person never
 * sees - let alone saves under their own name - somebody else's words.
 * Signing out drops every draft in this browser (`clearKeptDrafts`), since
 * none of it is the next person's to read.
 *
 * Storage is a convenience that can be missing (a private window, blocked
 * site data, a full quota), so every access is wrapped and a failure only
 * means the second copy is not there; memory carries on.
 *
 * Whether the browser asks before the tab closes is the save bar's
 * (`features/save-bar`): a draft not yet saved, or waiting for the person's
 * choice, keeps the bar up, and the bar's `beforeunload` is the one listener.
 *
 * Module state on purpose: it has to outlive the page, which is the whole
 * point.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { explicitLineDurationMs, type StoryboardDocument } from "@scandi/shared";
import { findLine, findShot } from "@/features/storyboard/document";

/** Which stored value a draft is a new version of, and that version as it would be sent. */
export interface DraftProbe {
  entity: "line" | "shot";
  /** The line's or the shot's key. */
  key: string;
  field: "text" | "speaker" | "delivery" | "durationMs" | "noteMd" | "description" | "action" | "cutToMs";
  /** The value as the write would send it. */
  wire: unknown;
}

export interface KeptDraft {
  /** The person's value, as the field holds it. */
  value: unknown;
  /** The stored value the draft was started from: what a clash is measured against. */
  origin: { value: unknown } | null;
  /** Somebody else changed this field too, and the person has not chosen yet. */
  clash: boolean;
  /** How a person calls the line: "Line 3", "Hook 2". */
  label: string;
  /** What it is a draft of, for a page that does not have the field. */
  probe?: DraftProbe | null;
}

/** Takes a draft left by an earlier visit up into its field. */
export type AdoptDraft = (draft: KeptDraft) => void;

/** The navigation state a line page reads to put the words left on it back as it opens ("Open" on the line). */
export const RESTORE_DRAFTS_STATE = "restoreDrafts";

/** A line of the storyboard with drafts left on it whose fields are not on this page. */
export interface LeftLine {
  lineKey: string;
  /** "Line 3", "Hook 2", as it was called when the words were typed. */
  label: string;
}

/** The copy in storage: the draft, when it was last written, and by which tab. */
interface StoredDraft extends KeptDraft {
  at: number;
  tab: string;
  /** The account that typed it (`setDraftOwner`). */
  user: string | null;
}

const STORAGE_PREFIX = "scandi.draft:";
const ALIVE_PREFIX = "scandi.draft-tab:";
/** How long a left draft is kept for: a week, then it is dropped unread. */
const EXPIRES_MS = 7 * 24 * 60 * 60 * 1000;
/** How often a tab holding drafts says it is still here, and how long its silence takes to mean it is gone. */
const BEAT_MS = 15_000;
const ALIVE_MS = 45_000;

/** This page load: a reload is a new visit, and its old drafts are an earlier visit's. */
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const kept = new Map<string, KeptDraft>();
/** Drafts left by an earlier visit, each with the mounted field that would take it up. */
const offers = new Map<string, AdoptDraft>();
const listeners = new Set<() => void>();
/** Moves whenever the set of left drafts or offers changes, for `useSyncExternalStore`. */
let changes = 0;
let beating: number | null = null;
/** The account whose drafts this tab keeps and offers. */
let owner: string | null = null;

/* ---------------------------------------------------------------- storage */

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function store(key: string, draft: KeptDraft): void {
  try {
    const record: StoredDraft = { ...draft, at: Date.now(), tab: TAB_ID, user: owner };
    storage()?.setItem(STORAGE_PREFIX + key, JSON.stringify(record));
  } catch {
    // No room, or no storage: memory still has it.
  }
}

function unstore(key: string): void {
  try {
    storage()?.removeItem(STORAGE_PREFIX + key);
  } catch {
    // Nothing to remove from.
  }
}

function parse(raw: string | null): StoredDraft | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredDraft> | null;
    if (!parsed || typeof parsed !== "object" || !("value" in parsed)) return undefined;
    const origin = parsed.origin && typeof parsed.origin === "object" && "value" in parsed.origin ? parsed.origin : null;
    const probe =
      parsed.probe && typeof parsed.probe === "object" && typeof parsed.probe.key === "string" ? parsed.probe : null;
    return {
      value: parsed.value,
      origin,
      clash: parsed.clash === true,
      label: typeof parsed.label === "string" ? parsed.label : "",
      probe,
      at: typeof parsed.at === "number" ? parsed.at : 0,
      tab: typeof parsed.tab === "string" ? parsed.tab : "",
      user: typeof parsed.user === "string" ? parsed.user : null,
    };
  } catch {
    return undefined;
  }
}

/** A stored draft of the signed-in account, unless it has expired (then it is dropped on the spot). */
function readStored(key: string): StoredDraft | undefined {
  try {
    const draft = parse(storage()?.getItem(STORAGE_PREFIX + key) ?? null);
    if (draft && Date.now() - draft.at > EXPIRES_MS) {
      unstore(key);
      return undefined;
    }
    // Somebody else's words are never offered, nor touched.
    if (draft && (owner === null || draft.user !== owner)) return undefined;
    return draft;
  } catch {
    return undefined;
  }
}

/** The stored keys of one storyboard (`1290/`), without the prefix. */
function storedKeys(prefix: string): string[] {
  try {
    const s = storage();
    if (!s) return [];
    const keys: string[] = [];
    for (let index = 0; index < s.length; index += 1) {
      const name = s.key(index);
      if (name?.startsWith(STORAGE_PREFIX + prefix)) keys.push(name.slice(STORAGE_PREFIX.length));
    }
    return keys;
  } catch {
    return [];
  }
}

/** Whether the tab that wrote a draft is still open (this one always is). */
function tabAlive(tab: string): boolean {
  if (tab === TAB_ID) return true;
  try {
    const at = Number(storage()?.getItem(ALIVE_PREFIX + tab));
    return Number.isFinite(at) && at > 0 && Date.now() - at < ALIVE_MS;
  } catch {
    return false;
  }
}

/** Expired drafts and long-silent heartbeats, swept once when the app loads. */
function sweep(): void {
  try {
    const s = storage();
    if (!s) return;
    const stale: string[] = [];
    for (let index = 0; index < s.length; index += 1) {
      const name = s.key(index);
      if (name?.startsWith(STORAGE_PREFIX)) {
        const draft = parse(s.getItem(name));
        if (!draft || Date.now() - draft.at > EXPIRES_MS) stale.push(name);
      } else if (name?.startsWith(ALIVE_PREFIX)) {
        const at = Number(s.getItem(name));
        if (!Number.isFinite(at) || Date.now() - at > EXPIRES_MS) stale.push(name);
      }
    }
    for (const name of stale) s.removeItem(name);
  } catch {
    // Nothing to sweep.
  }
}
sweep();

/* --------------------------------------------------------------- the beat */

function writeBeat(): void {
  try {
    storage()?.setItem(ALIVE_PREFIX + TAB_ID, String(Date.now()));
  } catch {
    // Without a beat the drafts read as left a little early: still offered, never lost.
  }
}

function clearBeat(): void {
  try {
    storage()?.removeItem(ALIVE_PREFIX + TAB_ID);
  } catch {
    // Nothing to clear.
  }
}

/** The tab is going: its drafts are an earlier visit's from now on. */
function onPageHide(): void {
  clearBeat();
}

/** Back from the back-forward cache with drafts still held: still here. */
function onPageShow(): void {
  if (beating !== null) writeBeat();
}

/** Beat while this tab holds drafts; stop when it holds none. */
function syncBeat(): void {
  if (typeof window === "undefined") return;
  const need = kept.size > 0;
  if (need && beating === null) {
    writeBeat();
    beating = window.setInterval(writeBeat, BEAT_MS);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
  } else if (!need && beating !== null) {
    window.clearInterval(beating);
    beating = null;
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    clearBeat();
  }
}

function sync() {
  syncBeat();
}

function emit() {
  changes += 1;
  for (const listener of listeners) listener();
}

/* ---------------------------------------------------------------- owners */

/**
 * Whose drafts this tab keeps from now on: the signed-in account (its id, or
 * its role for a shared team login). Called while a storyboard page renders,
 * before its fields read their drafts; the same owner again does nothing. A
 * different one lets this visit's drafts of the previous account go from
 * memory (they stay in storage for that account to come back to).
 */
export function setDraftOwner(next: string): void {
  if (next === owner) return;
  const switched = owner !== null;
  owner = next;
  if (!switched) return;
  kept.clear();
  offers.clear();
  // Not during the render that switched: the listeners re-render on their own turn.
  queueMicrotask(() => {
    sync();
    emit();
  });
}

/** Signed out: every draft in this browser goes, kept or left, since none of it is the next person's. */
export function clearKeptDrafts(): void {
  kept.clear();
  offers.clear();
  owner = null;
  try {
    const s = storage();
    if (s) {
      const names: string[] = [];
      for (let index = 0; index < s.length; index += 1) {
        const name = s.key(index);
        if (name?.startsWith(STORAGE_PREFIX) || name?.startsWith(ALIVE_PREFIX)) names.push(name);
      }
      for (const name of names) s.removeItem(name);
    }
  } catch {
    // Nothing to clear.
  }
  sync();
  emit();
}

/* ------------------------------------------------------------ this visit */

/** The key of one field of one line: `1290/l03/text`. */
export function draftKey(storyboardId: number, lineKey: string, field: string): string {
  return `${storyboardId}/${lineKey}/${field}`;
}

/** The key of one field of one shot, filed under its line: `1290/l03/shot.s07.action`. */
export function shotDraftKey(storyboardId: number, lineKey: string, shotKey: string, field: string): string {
  return draftKey(storyboardId, lineKey, `shot.${shotKey}.${field}`);
}

export function keepDraft(key: string, draft: KeptDraft): void {
  kept.set(key, draft);
  store(key, draft);
  // Typed again over a draft left by an earlier visit: the new words are the ones to keep.
  if (offers.delete(key)) emit();
  sync();
}

/**
 * Let a draft of this visit go; with `only`, only while the value kept passes
 * it (still the one that was saved). A draft left by an earlier visit is not
 * this visit's to drop (`forgetLeftDraft`).
 */
export function dropDraft(key: string, only?: (value: unknown) => boolean): void {
  const draft = kept.get(key);
  if (!draft || (only && !only(draft.value))) return;
  kept.delete(key);
  unstore(key);
  sync();
}

/** A draft of this visit, for a field mounting again. */
export function readDraft(key: string): KeptDraft | undefined {
  return kept.get(key);
}

/** The lines of a storyboard waiting for the person's choice, by name. */
export function keptClashes(storyboardId: number): string[] {
  const prefix = `${storyboardId}/`;
  const labels = new Set<string>();
  for (const [key, draft] of kept) if (key.startsWith(prefix) && draft.clash) labels.add(draft.label);
  return [...labels];
}

/** The line a key is filed under. */
const lineOfKey = (key: string, prefix: string) => key.slice(prefix.length, key.lastIndexOf("/"));

/** Forget the drafts of lines a storyboard no longer has, kept or left: there is nowhere to put them. */
export function pruneDrafts(storyboardId: number, liveLines: ReadonlySet<string>): void {
  const prefix = `${storyboardId}/`;
  const gone = (key: string) => key.startsWith(prefix) && !liveLines.has(lineOfKey(key, prefix));
  let changed = false;
  for (const key of [...kept.keys()]) {
    if (!gone(key)) continue;
    kept.delete(key);
    changed = true;
  }
  let left = false;
  for (const key of storedKeys(prefix)) {
    if (!gone(key)) continue;
    unstore(key);
    left = true;
  }
  for (const key of [...offers.keys()]) if (gone(key)) left = offers.delete(key) || left;
  if (changed) sync();
  if (left) emit();
}

/* ------------------------------------------------- left by an earlier visit */

/** A stored draft no live tab holds: an earlier visit's. */
function leftStored(key: string): StoredDraft | undefined {
  if (kept.has(key)) return undefined;
  const draft = readStored(key);
  return draft && !tabAlive(draft.tab) ? draft : undefined;
}

/** A draft an earlier visit of the page left in storage and this visit has not taken up. */
export function readLeftDraft(key: string): KeptDraft | undefined {
  return leftStored(key);
}

/** The store has a left draft's words after all (the save on the way out arrived): nothing to offer. */
export function forgetLeftDraft(key: string): void {
  if (kept.has(key)) return;
  unstore(key);
  offers.delete(key);
  emit();
}

/**
 * Offer a left draft back: the page counts it in its "You left ..." line, and
 * Restore hands it to `adopt`. Returns the way to withdraw the offer (the
 * field unmounted, or its stored value changed and it looks again).
 */
export function offerDraft(key: string, adopt: AdoptDraft): () => void {
  offers.set(key, adopt);
  emit();
  return () => {
    if (offers.get(key) !== adopt) return;
    offers.delete(key);
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Two values the same as far as the store is concerned: blank is none, and spaces at the ends do not count. */
function sameValue(a: unknown, b: unknown): boolean {
  const norm = (value: unknown) => (typeof value === "string" ? value.trim() || null : (value ?? null));
  return Object.is(norm(a), norm(b));
}

/**
 * Where a left draft belongs on the document as it stands - the key of the
 * line whose page has its field - or null when there is nothing to ask
 * about: its line or shot is gone, or the store already holds the very value
 * the draft would send.
 */
function placeOf(
  doc: Pick<StoryboardDocument, "lines" | "looseShots">,
  key: string,
  prefix: string,
  draft: KeptDraft,
): string | null {
  const probe = draft.probe;
  if (!probe) {
    const lineKey = lineOfKey(key, prefix);
    return findLine(doc, lineKey) ? lineKey : null;
  }
  if (probe.entity === "line") {
    const line = findLine(doc, probe.key);
    if (!line) return null;
    const stored =
      probe.field === "text"
        ? line.text
        : probe.field === "speaker"
          ? line.speaker
          : probe.field === "delivery"
            ? line.delivery
            : probe.field === "durationMs"
              ? explicitLineDurationMs(line)
              : probe.field === "noteMd"
                ? (line.note?.bodyMd ?? null)
                : undefined;
    return sameValue(stored, probe.wire) ? null : line.key;
  }
  const shot = findShot(doc, probe.key);
  // A shot on no line has no line page to put the words back on.
  if (!shot || shot.line === null) return null;
  const stored =
    probe.field === "description"
      ? shot.description
      : probe.field === "action"
        ? shot.action
        : probe.field === "cutToMs"
          ? shot.cutToMs
          : undefined;
  return sameValue(stored, probe.wire) ? null : shot.line;
}

/**
 * The drafts an earlier visit left on one storyboard, for the page's line.
 *
 * `here` counts the ones whose field is on this page (Restore takes each up
 * into its field, which saves it on top or asks); `elsewhere` names the
 * lines whose drafts belong to fields this page does not have (their line
 * page puts them back). Drafts the document already agrees with, or whose
 * line or shot is gone, are let go without a word. `discard` lets every one
 * of them go. Restore answers the lines it put back.
 */
export function useLeftDrafts(
  storyboardId: number,
  doc: Pick<StoryboardDocument, "lines" | "looseShots"> | null,
) {
  const prefix = `${storyboardId}/`;
  const version = useSyncExternalStore(subscribe, () => changes);

  const state = useMemo(() => {
    // `version` is what says the module state moved; reading it ties the memo to it.
    void version;
    const here = [...offers.keys()].filter((key) => key.startsWith(prefix));
    const elsewhere: LeftLine[] = [];
    const settled: string[] = [];
    for (const key of storedKeys(prefix)) {
      if (offers.has(key)) continue;
      const draft = leftStored(key);
      if (!draft) continue;
      const lineKey = doc ? placeOf(doc, key, prefix, draft) : lineOfKey(key, prefix);
      if (lineKey === null) settled.push(key);
      else elsewhere.push({ lineKey, label: draft.label });
    }
    return { here: here.length, elsewhere, settled };
  }, [version, doc, prefix]);

  // Nothing to ask about: the save on the way out arrived, or the line went.
  useEffect(() => {
    for (const key of state.settled) forgetLeftDraft(key);
  }, [state.settled]);

  const lines = useMemo(() => {
    const seen = new Map<string, LeftLine>();
    for (const line of state.elsewhere) if (!seen.has(line.lineKey)) seen.set(line.lineKey, line);
    return [...seen.values()];
  }, [state.elsewhere]);

  const restore = useCallback((): string[] => {
    const restored: string[] = [];
    for (const [key, adopt] of [...offers]) {
      if (!key.startsWith(prefix)) continue;
      offers.delete(key);
      const draft = readStored(key);
      if (!draft) continue;
      kept.set(key, draft);
      // This tab holds it now: no other tab is to offer it while it is settled here.
      store(key, draft);
      adopt(draft);
      restored.push(lineOfKey(key, prefix));
    }
    sync();
    emit();
    return restored;
  }, [prefix]);

  const discard = useCallback(() => {
    for (const key of [...offers.keys()]) {
      if (!key.startsWith(prefix)) continue;
      offers.delete(key);
      unstore(key);
    }
    for (const key of storedKeys(prefix)) if (leftStored(key)) unstore(key);
    emit();
  }, [prefix]);

  return {
    count: state.here + state.elsewhere.length,
    here: state.here,
    elsewhere: lines,
    restore,
    discard,
  };
}
